/* eslint-disable react/jsx-no-bind */

import React from 'react';
import PropTypes from 'prop-types';
import { size, each, findIndex, isEqual, isEmpty, noop } from 'underscore';
import { List } from '@fluentui/react/lib/List';
import { FocusZone, FocusZoneDirection } from '@fluentui/react/lib/FocusZone';
import { MessageBar } from '@fluentui/react/lib/MessageBar';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { PageNavigationFooter } from '@bingads-webui-campaign-react/smart-component-primitive';

import { ToolCell } from './tool-cell';
import { PickerCell } from './picker-cell';

const getStyles = (_, { palette = {} }) => ({
  listContainer: {
    background: '#F3F2F0',
    paddingTop: 32,
    marginTop: 16,

    '& .ms-List-cell': {
      verticalAlign: 'top',
      width: 374,
      marginBottom: 24,
      textAlign: 'center',
      marginLeft: 52,
      marginRight: 70,
    },
  },
  cellContainer: {
    background: '#FFFFFF',
    boxShadow: '0px 1.6px 3.6px rgba(0, 0, 0, 0.132), 0px 0.3px 0.9px rgba(0, 0, 0, 0.108)',
    borderRadius: 2,
    width: '100%',
    textAlign: 'left',
  },
  emptyTips: {
    padding: '20px 0',

    '& .ms-MessageBar-content': {
      background: palette.themeLighterAlt,
    },
  },
  countIndicator: {
    margin: '0 10px 5px 0',
    textAlign: 'right',
    display: 'block',
    fontWeight: 'bold',
  },
});

class UnStyledItemPicker extends React.PureComponent {
  constructor(props) {
    super(props);

    const { items, numberOfItemsPerPage } = this.props;
    const pages = [];
    let currentPage = [];

    each(items, (item) => {
      if (currentPage.length < numberOfItemsPerPage) {
        currentPage.push(item);
      } else {
        pages.push(currentPage);
        currentPage = [item];
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    this.state = {
      originalItems: [...items],
      selectedPageIndex: 0,
      visibleItems: pages[0],
      pages,
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { items, numberOfItemsPerPage } = nextProps;
    if (!isEqual(items, prevState.originalItems)) {
      const pages = [];
      let currentPage = [];
      let newSelectedPageIndex = prevState.selectedPageIndex;

      each(items, (item) => {
        if (currentPage.length < numberOfItemsPerPage) {
          currentPage.push(item);
        } else {
          pages.push(currentPage);
          currentPage = [item];
        }
      });

      if (currentPage.length > 0) {
        pages.push(currentPage);
      }

      if (newSelectedPageIndex >= pages.length && newSelectedPageIndex > 0) {
        newSelectedPageIndex = pages.length - 1;
      }

      return {
        originalItems: [...items],
        selectedPageIndex: newSelectedPageIndex,
        pages,
        visibleItems: pages[newSelectedPageIndex],
      };
    }
    return null;
  }

  onRenderCell(item, index) {
    const {
      ItemComponent,
      selectable,
      onDelete,
      onEdit,
      i18n,
      classes,
      getDeleteButtonAriaLabel,
      getEditButtonAriaLabel,
      editable,
      editableConditionCheck,
      editButtonLabel,
    } = this.props;

    const commonCellProps = {
      ItemComponent,
      item,
      i18n,
      className: classes.cellContainer,
    };

    if (selectable) {
      return (
        <PickerCell
          {...commonCellProps}
          selectable={!!item.selectable}
          defaultState={!!item.selected}
          updateSelectedState={this.updateSelectedState.bind(this)}
        />
      );
    }

    return (
      <ToolCell
        {...commonCellProps}
        onDelete={onDelete}
        onEdit={onEdit}
        index={index}
        editable={editable && editableConditionCheck(item)}
        selectedPageIndex={this.state.selectedPageIndex}
        numberOfItemsPerPage={this.props.numberOfItemsPerPage}
        editButtonLabel={editButtonLabel}
        deleteButtonAriaLabel={getDeleteButtonAriaLabel(index + 1)}
        editButtonAriaLabel={getEditButtonAriaLabel(index + 1)}
      />
    );
  }

  getItemCountForPage() {
    return this.props.numberOfItemsPerPage;
  }

  getPageHeight() {
    return 400;
  }

  updateSelectedState({ id }) {
    const { onSelectChange, limit, selectedIds } = this.props;

    this.setState((state) => {
      const visibleItems = [...state.visibleItems];
      const foundIndex = findIndex(visibleItems, ({ id: itemId }) => itemId === id);
      visibleItems[foundIndex] = {
        ...visibleItems[foundIndex],
        selected: !visibleItems[foundIndex].selected,
      };

      const currentIsSelected = visibleItems[foundIndex].selected;
      if (currentIsSelected && size(selectedIds) + 1 === limit) {
        visibleItems.forEach((item, index) => { visibleItems[index].selectable = item.selected; });
      } else {
        visibleItems.forEach((_, index) => { visibleItems[index].selectable = true; });
      }

      if (onSelectChange) {
        onSelectChange({ id, isChecked: visibleItems[foundIndex].selected });
      }

      return {
        visibleItems,
      };
    });
  }

  updatePage(newPageIndex) {
    this.setState(state => ({
      visibleItems: state.pages[newPageIndex],
      selectedPageIndex: newPageIndex,
    }));
  }

  goToNextPage() {
    this.updatePage(this.state.selectedPageIndex + 1);
  }

  goToPreviousPage() {
    this.updatePage(this.state.selectedPageIndex - 1);
  }

  render() {
    const {
      classes,
      i18n,
      items,
      selectable,
      selectedIds,
      limit,
      noDataLabel,
    } = this.props;
    const { visibleItems, selectedPageIndex, pages } = this.state;

    return (
      isEmpty(items) ?
        <div className={classes.emptyTips}>
          <MessageBar isMultiline>
            {i18n.getString(noDataLabel)}
          </MessageBar>
        </div> :
        <div className={classes.listContainer}>
          <FocusZone direction={FocusZoneDirection.vertical}>
            <List
              items={visibleItems}
              getItemCountForPage={this.getItemCountForPage.bind(this)}
              onRenderCell={this.onRenderCell.bind(this)}
              ignoreScrollingState
              getPageHeight={this.getPageHeight}
            />
            {
              selectable &&
              <span className={classes.countIndicator}>
                {`${size(selectedIds)}/${limit} ${i18n.getString(_TL_('Selected'))}`}
              </span>
            }
          </FocusZone>
          <PageNavigationFooter
            i18n={i18n}
            totalPageNumber={pages.length}
            onNextPageClick={this.goToNextPage.bind(this)}
            onPreviousPageClick={this.goToPreviousPage.bind(this)}
            onRandomPageSelect={this.updatePage.bind(this)}
            pageIndex={selectedPageIndex}
          />
        </div>
    );
  }
}

UnStyledItemPicker.propTypes = {
  classes: PropTypes.shape({
    listContainer: PropTypes.string.isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  ItemComponent: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  numberOfItemsPerPage: PropTypes.number,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onSelectChange: PropTypes.func,
  selectable: PropTypes.bool,
  getDeleteButtonAriaLabel: PropTypes.func,
  editButtonLabel: PropTypes.string,
  getEditButtonAriaLabel: PropTypes.func,
  limit: PropTypes.number,
  selectedIds: PropTypes.shape({}),
  noDataLabel: PropTypes.string,
  // Overall edit function
  editable: PropTypes.bool,
  // Each item edit support check
  editableConditionCheck: PropTypes.func,
};

UnStyledItemPicker.defaultProps = {
  numberOfItemsPerPage: 2,
  onDelete: null,
  onEdit: null,
  onSelectChange: null,
  selectable: false,
  limit: Infinity,
  selectedIds: {},
  getDeleteButtonAriaLabel: noop,
  getEditButtonAriaLabel: noop,
  noDataLabel: _TL_('There are no Facebook posts to import from the last 90 days.'),
  editable: false,
  editableConditionCheck: () => true,
  editButtonLabel: null,
};

export const ItemPicker = withDefaultStyles(
  UnStyledItemPicker,
  getStyles
);
